import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CancelSessionDialog from '../tutor/CancelSessionDialog';
import SessionMaterialsList from '../SessionMaterialsList';
import TutorProfileAvatar from '../TutorProfileAvatar';
import { useSystemMessage } from '../../contexts/SystemMessageContext';
import {
  cancelTutorSession,
  getTutorUpcomingSessions,
  TUTOR_UPCOMING_PAGE_SIZE,
} from '../../services/tutorBookingService';
import {
  getBookingStatusLabel,
  getBookingSubjectName,
} from '../../utils/bookingDisplay';
import { getCurrentUser } from '../../utils/api';
import { openTutorBookingConversation } from '../../services/messagingService';

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatTime = (value) =>
  new Date(value).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

const getLearnerFirstName = (session) =>
  session?.learnerLabel?.trim().split(/\s+/)[0] ||
  session?.student?.firstName ||
  'Learner';

const getLearnerPhoto = (session) =>
  session?.student?.profilePhotoUrl || session?.student?.avatarIcon;

const statusClass = (status) => {
  if (status === 'confirmed') return 'bg-green-100 text-green-800';
  if (status === 'tutor_review') return 'bg-blue-100 text-blue-800';
  return 'bg-amber-100 text-amber-800';
};

export const TutorUpcomingSessionsPage = () => {
  const { showMessage } = useSystemMessage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Number.parseInt(searchParams.get('page'), 10);
  const [page, setPage] = useState(
    Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1,
  );
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionToCancel, setSessionToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [openingConversationId, setOpeningConversationId] = useState(null);

  const loadSessions = useCallback(async () => {
    const user = getCurrentUser();
    if (!user?.prospectId) {
      setError('Tutor profile not found. Please sign in again.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getTutorUpcomingSessions(user.prospectId, {
        page,
        limit: TUTOR_UPCOMING_PAGE_SIZE,
      });
      const rows = Array.isArray(response)
        ? response.slice(
            (page - 1) * TUTOR_UPCOMING_PAGE_SIZE,
            page * TUTOR_UPCOMING_PAGE_SIZE,
          )
        : response.data || response.sessions || [];
      const responseTotal = Array.isArray(response)
        ? response.length
        : Number(response.total) || 0;
      const responseTotalPages = Array.isArray(response)
        ? Math.ceil(responseTotal / TUTOR_UPCOMING_PAGE_SIZE)
        : Number(response.totalPages) || 0;
      const lastPage = Math.max(responseTotalPages, 1);

      if (page > lastPage) {
        setPage(lastPage);
        return;
      }

      setSessions(rows);
      setTotal(responseTotal);
      setTotalPages(responseTotalPages);
    } catch (loadError) {
      console.error('Failed to load tutor upcoming sessions:', loadError);
      setError(
        loadError.message ||
          'Unable to load upcoming sessions. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (
      Number.isInteger(requestedPage) &&
      requestedPage > 0 &&
      requestedPage !== page
    ) {
      setPage(requestedPage);
    }
  }, [page, requestedPage]);

  const requestedSessionId = Number.parseInt(searchParams.get('session'), 10);
  const selectedSession = useMemo(
    () =>
      sessions.find((session) => session.id === requestedSessionId) ||
      sessions[0] ||
      null,
    [requestedSessionId, sessions],
  );

  useEffect(() => {
    if (
      !selectedSession ||
      selectedSession.id === requestedSessionId
    ) {
      return;
    }
    setSearchParams(
      { page: String(page), session: String(selectedSession.id) },
      { replace: true },
    );
  }, [page, requestedSessionId, selectedSession, setSearchParams]);

  const selectSession = (sessionId) => {
    setSearchParams(
      { page: String(page), session: String(sessionId) },
      { replace: true },
    );
  };

  const changePage = (nextPage) => {
    if (loading || nextPage < 1 || nextPage > totalPages || nextPage === page) {
      return;
    }
    setSearchParams({ page: String(nextPage) }, { replace: true });
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmCancellation = async () => {
    if (!sessionToCancel) return;

    try {
      setCancelling(true);
      await cancelTutorSession(sessionToCancel.id);
      setSessionToCancel(null);
      showMessage({
        type: 'success',
        title: 'Session cancelled',
        message: 'The learner has been notified about the cancellation.',
      });

      if (sessions.length === 1 && page > 1) {
        const previousPage = page - 1;
        setSearchParams({ page: String(previousPage) }, { replace: true });
        setPage(previousPage);
      } else {
        await loadSessions();
      }
    } catch (cancelError) {
      console.error('Failed to cancel tutor session:', cancelError);
      showMessage({
        type: 'error',
        title: 'Unable to cancel session',
        message:
          cancelError.message ||
          'The session could not be cancelled. Please try again.',
      });
    } finally {
      setCancelling(false);
    }
  };

  const messageLearner = async (session) => {
    try {
      setOpeningConversationId(session.id);
      const conversation = await openTutorBookingConversation(session.id);
      if (!conversation?.id) {
        throw new Error('Conversation could not be opened.');
      }
      navigate(`/messages?conversation=${conversation.id}`);
    } catch (conversationError) {
      console.error('Failed to open learner conversation:', conversationError);
      showMessage({
        type: 'error',
        title: 'Unable to open conversation',
        message:
          conversationError.message ||
          'The learner chat could not be opened. Please try again.',
      });
    } finally {
      setOpeningConversationId(null);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-brand-deep-navy">
            Upcoming Sessions
          </h1>
          <p className="mt-2 text-gray-600">
            View and manage your upcoming tutoring sessions.
          </p>
        </div>
        <Link
          to="/tutor-dashboard"
          className="text-sm font-medium text-gray-600 hover:text-brand-dark-blue"
        >
          ← Back to Dashboard
        </Link>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
          <p>{error}</p>
          <button
            type="button"
            onClick={loadSessions}
            className="mt-3 font-semibold underline"
          >
            Try again
          </button>
        </div>
      ) : loading && sessions.length === 0 ? (
        <div className="flex min-h-72 items-center justify-center">
          <div
            className="h-10 w-10 animate-spin rounded-full border-b-4 border-brand-medium-blue"
            aria-label="Loading upcoming sessions"
          />
        </div>
      ) : sessions.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center text-gray-600 shadow-sm">
          You have no upcoming sessions.
        </div>
      ) : (
        <>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(340px,1fr)]">
            <section
              className="h-fit overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
              aria-label="Tutor upcoming sessions"
              aria-busy={loading}
            >
              <div className="border-b border-gray-200 px-5 py-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Upcoming Sessions
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {total} {total === 1 ? 'session' : 'sessions'} scheduled
                </p>
              </div>

              <div
                className={`divide-y divide-gray-100 transition-opacity ${
                  loading ? 'opacity-60' : 'opacity-100'
                }`}
              >
                {sessions.map((session) => {
                  const learnerName = getLearnerFirstName(session);
                  const selected = selectedSession?.id === session.id;
                  return (
                    <button
                      key={session.id}
                      id={`session-${session.id}`}
                      type="button"
                      onClick={() => selectSession(session.id)}
                      className={`w-full px-5 py-4 text-left transition-colors ${
                        selected
                          ? 'bg-blue-50 ring-2 ring-inset ring-brand-medium-blue/30'
                          : 'hover:bg-gray-50'
                      }`}
                      aria-pressed={selected}
                    >
                      <div className="flex items-center gap-3">
                        <TutorProfileAvatar
                          profilePic={getLearnerPhoto(session)}
                          name={learnerName}
                          className="h-11 w-11"
                          showInitialFallback
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {learnerName}
                            </span>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(session.status)}`}
                            >
                              {getBookingStatusLabel(session.status)}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-600">
                            {formatDate(session.startTime)} ·{' '}
                            {formatTime(session.startTime)} –{' '}
                            {formatTime(session.endTime)}
                          </p>
                          <p className="mt-1 truncate text-sm text-gray-600">
                            {getBookingSubjectName(session)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <nav
                  className="flex items-center justify-between border-t border-gray-200 px-5 py-4"
                  aria-label="Upcoming sessions pagination"
                >
                  <span className="text-sm text-gray-500">
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => changePage(page - 1)}
                      disabled={page === 1 || loading}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      ← Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => changePage(page + 1)}
                      disabled={page === totalPages || loading}
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Next →
                    </button>
                  </div>
                </nav>
              )}
            </section>

            <aside className="h-fit rounded-xl border border-gray-200 bg-white p-6 shadow-sm lg:sticky lg:top-24">
              <h2 className="text-xl font-semibold text-gray-900">
                Session Details
              </h2>

              <div className="mt-5 flex items-center gap-3 border-b border-gray-100 pb-5">
                <TutorProfileAvatar
                  profilePic={getLearnerPhoto(selectedSession)}
                  name={getLearnerFirstName(selectedSession)}
                  className="h-12 w-12"
                  showInitialFallback
                />
                <div>
                  <p className="font-semibold text-gray-900">
                    {getLearnerFirstName(selectedSession)}
                  </p>
                  {selectedSession.learnerLabel && (
                    <p className="text-sm text-gray-500">
                      Profile: {selectedSession.learnerLabel}
                    </p>
                  )}
                </div>
              </div>

              <dl className="mt-5 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-1">
                <div>
                  <dt className="font-medium text-gray-500">Subject</dt>
                  <dd className="mt-1 text-gray-900">
                    {getBookingSubjectName(selectedSession)}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Date</dt>
                  <dd className="mt-1 text-gray-900">
                    {formatDate(selectedSession.startTime)}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Time</dt>
                  <dd className="mt-1 text-gray-900">
                    {formatTime(selectedSession.startTime)} –{' '}
                    {formatTime(selectedSession.endTime)}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 text-gray-900">
                    {getBookingStatusLabel(selectedSession.status)}
                  </dd>
                </div>
                {selectedSession.referenceNumber && (
                  <div>
                    <dt className="font-medium text-gray-500">Reference</dt>
                    <dd className="mt-1 text-gray-900">
                      {selectedSession.referenceNumber}
                    </dd>
                  </div>
                )}
              </dl>

              <div className="mt-6 border-t border-gray-100 pt-5">
                <h3 className="mb-3 font-semibold text-gray-900">Materials</h3>
                <SessionMaterialsList
                  materials={selectedSession.materials}
                />
              </div>

              <div className="mt-6 border-t border-gray-100 pt-5">
                <h3 className="mb-2 font-semibold text-gray-900">
                  Learner Comments
                </h3>
                <p className="whitespace-pre-wrap text-sm text-gray-600">
                  {selectedSession.notes || 'No comments added.'}
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 border-t border-gray-100 pt-5">
                {selectedSession.status === 'confirmed' &&
                  selectedSession.hostStartUrl && (
                    <a
                      href={selectedSession.hostStartUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg bg-brand-medium-blue px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark-blue"
                    >
                      Start Session
                    </a>
                  )}
                <button
                  type="button"
                  onClick={() => messageLearner(selectedSession)}
                  disabled={openingConversationId === selectedSession.id}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  {openingConversationId === selectedSession.id
                    ? 'Opening Chat…'
                    : 'Message Learner'}
                </button>
                <Link
                  to="/tutor/availability"
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Manage Availability
                </Link>
                {selectedSession.status === 'confirmed' && (
                  <button
                    type="button"
                    onClick={() => setSessionToCancel(selectedSession)}
                    className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
                  >
                    Cancel Session
                  </button>
                )}
              </div>
            </aside>
          </div>
        </>
      )}

      <CancelSessionDialog
        open={Boolean(sessionToCancel)}
        session={sessionToCancel}
        loading={cancelling}
        onClose={() => setSessionToCancel(null)}
        onConfirm={confirmCancellation}
      />
    </main>
  );
};
